import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, CheckIcon, ChevronLeftIcon, DocumentIcon, EyeIcon, HourglassIcon, TrashIcon, UserIcon, UsersIcon } from '../../icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStickyOffset } from '../../../hooks/useStickyOffset';
import {
  PreviousFindingCard,
  PreviousFindingDetailModal,
  ContributorsBar,
  PhotoViewerWithEditor,
  PhotoViewer,
  BulkActionConfirmDialog,
  ConfirmDialog,
  AddFindingSheet,
  HazardResolutionModal,
  ScheduleChangeRequestModal,
  ScheduleChangeRequestPanel,
} from '../shared';
import { getScheduleChangeRequests, updateInspection } from '../../../lib/inspections';
import {
  activeFindings,
  countActiveFindings,
  countSelectedMeasureFindings,
  hasSelectedMeasure,
} from '../../../lib/findingVisibility';
// 게이트 판정은 **shared가 정본**이다(서버 집행·목록·스케줄이 같은 함수를 쓴다 — 2026-10-09).
// `REPORT_DISABLED_REASON_ID`만 web 전용(DOM id)이라 `lib/reportGate`에 남아 있다.
import {
  checkInspectionCompletability,
  resolveReportGate,
  signatureProgressLabel,
} from '@safetyhub/shared';
import { REPORT_DISABLED_REASON_ID } from '../../../lib/reportGate';
import { getRiskAssessmentConfigs } from '../../../lib/risk-assessment-config';
import { useTabScrollMemory } from '../../../hooks/useTabScrollMemory';
import type { BulkActionType, HazardEditState } from '../shared';
import type { BoundingBox, AnalysisStatus as LibAnalysisStatus, FindingSeverity as LibFindingSeverity } from '../../../lib/inspections';
import { PortalTooltip, InspectionPhotoSection } from '../desktop';
import { ReportTypeSelectModal } from '../../reports/ReportTypeSelectModal';
import { ReviewRequestResult, canStartScheduledInspection, InspectionCollaborator } from '../../../lib/inspections';
import ParticipantsEditModal from '../../modals/ParticipantsEditModal';
import { InspectionViewer } from '../../../types/websocket';
import { useAuthStore } from '../../../stores/auth';
import { usePermissions } from '../../../hooks/usePermissions';
import { SignatureModal } from '../../common';
import { formatDateToLocal } from '@safetyhub/shared';
import { Badge, Button, Modal } from '@safetyhub/ui';
import { MODAL_LAYER } from '../../modalLayers';
import { useToastStore } from '../../../stores/toast';
import type {
  CompletionState,
  Photo,
  PreviousFinding,
  InspectionDetailData,
  FindingEditHandlers,
} from './inspection-detail.types';

/**
 * 시안 `.btn-delete-insp` — 흰 바탕 rose 테두리/글자.
 * 활성/비활성(툴팁) 두 분기가 같은 버튼을 그리므로 클래스를 한 곳에서만 쓴다.
 */
const DELETE_INSPECTION_BUTTON_CLASS =
  'inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-rose-200 bg-white px-3.5 text-[13.5px] font-[650] tracking-[-0.005em] text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50';

/** 시안 `.role-tab` — 담당자 행 우측의 저강도 버튼. */
const ROLE_TAB_CLASS =
  'inline-flex h-[34px] items-center gap-1.5 rounded-card-sm border border-transparent px-3 text-[13.5px] font-semibold tracking-[-0.005em] text-ink-600 transition-colors hover:bg-surface-soft hover:text-ink-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent';

/**
 * 시안 `.insp-action-bar` — 전폭 바가 아니라 컬럼 폭 안에 떠 있는 blur 카드.
 * 아래 그림자의 위쪽 성분(`0 -2px 6px`)이 카드 밑으로 지나가는 콘텐츠와의 경계를 만든다.
 */
const ACTION_BAR_CARD_CLASS =
  'flex flex-wrap items-center justify-between gap-[14px] rounded-[14px] border border-line bg-white/[0.92] px-5 py-3.5 shadow-[0_-2px_6px_rgba(15,23,42,0.04),0_12px_24px_-10px_rgba(15,23,42,0.18)] backdrop-blur-[8px]';

/** 시안 `.btn-ghost` — 액션바 좌측의 선택 액션(재분석·검토·삭제). */
const ACTION_BAR_GHOST_CLASS =
  'inline-flex h-[34px] items-center gap-[5px] rounded-card-sm border border-line bg-white px-3 text-[13px] font-[650] tracking-[-0.005em] text-ink-600 transition-colors enabled:hover:border-ink-300 enabled:hover:bg-surface-soft enabled:hover:text-ink-800 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * 시안 `.btn-complete` — 점검완료. 액션바에서 유일한 채색 액션이라
 * 전역 `Button`의 success 결이 아니라 시안 그라데이션을 로컬로 소유한다.
 */
const ACTION_BAR_COMPLETE_CLASS =
  'inline-flex h-[38px] items-center gap-1.5 rounded-control bg-gradient-to-br from-emerald-600 to-emerald-500 px-[18px] text-sm font-bold tracking-[-0.005em] text-white shadow-[0_6px_14px_-6px_rgba(5,150,105,0.5)] transition enabled:hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50';

/** 시안 `.btn-report`/`.btn-tbm` — 액션바 우측의 흰 바탕 액션. */
const ACTION_BAR_OUTLINE_CLASS =
  'inline-flex h-[38px] items-center gap-1.5 rounded-control border border-line bg-white px-3.5 text-[13.5px] font-[650] tracking-[-0.005em] text-ink-700 transition-colors enabled:hover:border-ink-300 enabled:hover:bg-surface-soft enabled:hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-[0.55]';

/** 시안 `.action-hint` — 액션바 우측 안내 문구. */
const ACTION_HINT_CLASS = 'text-[13px] font-medium tracking-[-0.005em]';

interface InspectionDetailDesktopProps {
  inspection: InspectionDetailData;
  /** 현재 사용자 ID (권한 체크용) */
  currentUserId?: string;
  /** 조직 Owner 여부 (권한 체크용) */
  isOwner?: boolean;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (measure 해제 override 권한) */
  isPrimary?: boolean;
  /** 점검 주 담당자 ID (권한 체크용) */
  inspectorId?: string;
  /** 읽기 전용 모드 (비담당자 접근 또는 완료된 점검) */
  isReadonly?: boolean;
  /** SCHEDULED 상태 여부 (읽기전용이지만 점검 시작 버튼 필요) */
  isScheduled?: boolean;
  /** 보고서 생성 여부 (생성된 경우 사진 추가 불가) */
  hasReport?: boolean;
  /** 이후 차수 점검이 존재하여 수정 불가 여부 */
  isLockedByLaterInspection?: boolean;
  /** AI 분석 활성화 여부 */
  enableAIAnalysis?: boolean;
  /** AI 분석 활성화 여부 변경 */
  setEnableAIAnalysis?: (enabled: boolean) => void;
  /** AI 분析 중점 카테고리 */
  focusCategories?: string[];
  /** AI 분析 중점 카테고리 변경 */
  setFocusCategories?: (categories: string[]) => void;
  onAddPhoto: () => void;
  /** 우수사례 사진 추가 */
  onAddBestPracticePhoto?: () => void;
  /** 카메라 촬영 모달 열기 */
  onOpenCamera?: () => void;
  onPhotoReview: (photoId: string, reviewed: boolean) => void;
  /** 선택된 사진들 검토 완료 */
  onSelectiveReview: (photoIds: string[]) => void;
  /** 선택된 사진들 삭제 */
  onSelectiveDelete: (photoIds: string[]) => void;
  /** 선택된 사진들 재분석 */
  onBulkReanalyze: (photoIds: string[]) => void;
  onDeleteInspection: () => void;
  onResolveFindings: (findingId: string, data: {
    resolutionPhotoUrl?: string;
    resolutionPhotoId?: string;
    resolutionNotes?: string;
  }) => void;
  onUnresolveFindings: (findingId: string) => void;
  onRequestResolutionPhoto: (findingId: string) => void;
  onPhotoDelete: (photoId: string) => void;
  onPhotoReanalyze: (photoId: string) => void;
  /** 분석 범위 지정 모달의 대상을 지목한다(모달은 점검 상세 페이지가 하나만 들고 있다) */
  onPhotoReanalyzeWithRegion: (photoId: string) => void;
  onPhotoDownload: (photoId: string) => void;
  onBack: () => void;
  isLoading?: boolean;
  isReanalyzing?: boolean;
  /** 재분석 진행 상황 */
  reanalysisProgress?: { current: number; total: number };
  /** 발견사항 편집 핸들러 */
  findingEditHandlers?: FindingEditHandlers;
  /** 현재 점검을 보고 있는 사용자 목록 */
  viewers?: InspectionViewer[];
  /** 검토 요청 */
  onRequestReview?: () => Promise<ReviewRequestResult | null>;
  /** 발견사항 추가 (새 발견사항 ID 반환) */
  onAddFinding?: (photoId: string, data: {
    category: string;
    description: string;
  }) => Promise<string | void>;
  /** 발견사항 보고서 포함 선택 기능 활성화 여부 */
  findingSelectionEnabled?: boolean;
  /** 개선대책 보고서 포함 여부 토글 */
  onToggleMeasureSelection?: (findingId: string, measure: string, excluded: boolean) => void;
  /** 점검완료 서명 워크플로우 */
  completion?: CompletionState;
  /** 예약 점검 시작 핸들러 */
  onStartScheduled?: () => void;
  /** 사진 메타 업데이트 핸들러 */
  onPhotoMetaUpdate?: (photoId: string, updates: {
    location?: { id: string; name: string } | null;
    detailLocation?: { id: string; name: string } | null;
    equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
    processType?: string | null;
  }) => void;
  /** 현장 담당자 모달 열기 */
  onOpenSiteContactsModal?: () => void;
}

export function InspectionDetailDesktop({
  inspection,
  currentUserId,
  isOwner = false,
  isPrimary = false,
  inspectorId,
  isReadonly = false,
  isScheduled = false,
  hasReport = false,
  isLockedByLaterInspection = false,
  enableAIAnalysis = true,
  setEnableAIAnalysis,
  focusCategories = [],
  setFocusCategories,
  onAddPhoto,
  onAddBestPracticePhoto,
  onOpenCamera,
  onPhotoReview,
  onSelectiveReview,
  onSelectiveDelete,
  onBulkReanalyze,
  onDeleteInspection,
  onResolveFindings: _onResolveFindings,
  onUnresolveFindings: _onUnresolveFindings,
  onRequestResolutionPhoto: _onRequestResolutionPhoto,
  onPhotoDelete,
  onPhotoReanalyze,
  onPhotoReanalyzeWithRegion,
  onPhotoDownload,
  onBack,
  isLoading = false,
  isReanalyzing = false,
  reanalysisProgress,
  findingEditHandlers,
  viewers = [],
  onRequestReview,
  onAddFinding,
  findingSelectionEnabled = false,
  onToggleMeasureSelection,
  completion,
  onStartScheduled,
  onPhotoMetaUpdate,
  onOpenSiteContactsModal,
}: InspectionDetailDesktopProps) {
  const { currentMembership } = useAuthStore();
  const { canManageInspections, isSiteAssigned } = usePermissions(inspection.siteId);
  const queryClient = useQueryClient();
  const companyId = currentMembership?.companyId;
  const { showToast } = useToastStore();

  // 스케줄 변경 요청 모달 상태
  const [showScheduleChangeModal, setShowScheduleChangeModal] = useState(false);

  // 조직의 활성 위험성평가 config 목록 (드롭다운용)
  const { data: riskConfigsData } = useQuery({
    queryKey: ['riskAssessmentConfigs'],
    queryFn: getRiskAssessmentConfigs,
    staleTime: 5 * 60 * 1000,
  });
  const allRiskConfigs = riskConfigsData?.data ?? [];

  // 점검별 위험성평가 config 변경 핸들러
  const handleRiskConfigChange = useCallback(async (configId: string | null) => {
    if (!companyId) return;
    await updateInspection(inspection.id, { riskAssessmentConfigId: configId }, companyId);
    await queryClient.invalidateQueries({ queryKey: ['inspection', inspection.id] });
  }, [inspection.id, companyId, queryClient]);

  // 스케줄 변경 요청 목록 조회 (SCHEDULED 상태에서만)
  const { data: scheduleChangeRequestsData, refetch: refetchScheduleChangeRequests } = useQuery({
    queryKey: ['scheduleChangeRequests', inspection.id],
    queryFn: () => getScheduleChangeRequests(inspection.id),
    enabled: !!inspection.id && inspection.status === 'SCHEDULED',
  });
  const scheduleChangeRequests = scheduleChangeRequestsData?.success && scheduleChangeRequestsData.data
    ? scheduleChangeRequestsData.data
    : [];

  // 변경 요청 버튼 표시 조건:
  // - SCHEDULED 상태
  // - assignedById 있음 (Owner가 배정)
  // - 본인이 inspector
  const canRequestScheduleChange = inspection.status === 'SCHEDULED'
    && !!inspection.assignedById
    && inspectorId === currentUserId
    && !isOwner;

  // 사진 배열 분리 (위험요인 / 우수사례)
  const hazardPhotos = inspection.photos.filter(p => p.purpose !== 'BEST_PRACTICE');
  const bestPracticePhotos = inspection.photos.filter(p => p.purpose === 'BEST_PRACTICE');

  // 서브헤더(시안 `.insp-subheader`)는 정적이라 스크롤하면 그대로 올라간다 — 그 아래 sticky
  // 요소들의 기준 top은 뷰포트 상단이다. 데스크톱은 Layout 헤더가 `lg:hidden`이라 위에
  // 고정된 것이 없으므로 0. (예전엔 sticky 헤더 높이를 실측해 넘겼다.)
  const sectionStickyTop = 0;
  // 탭 바 하단 위치 → 금번 점검 섹션 헤더의 sticky top으로 사용
  const { ref: tabBarRef, stickyTop: tabBarBottom } = useStickyOffset<HTMLDivElement>();

  // 편집 가능 여부 (읽기 전용이 아닌 경우 - COMPLETED는 상태 표시일 뿐 편집 제한 아님)
  const canEdit = !isReadonly;

  // 점검완료 잠금 — 명시적으로 "점검완료 취소"를 해야 편집 가능
  const isCompletionLocked = !!completion?.isMyCompleted;

  // 사진/발견사항 편집 공통 비활성화 조건
  const isEditDisabled = hasReport || isLockedByLaterInspection;

  // 발견사항 편집 가능 여부 (보고서 생성 또는 점검완료 잠금 시 편집 불가)
  const canEditFinding = canEdit && !isEditDisabled && !isCompletionLocked;

  // 위험영역 편집 가능 여부
  const canEditHazard = canEdit && !isEditDisabled && !isCompletionLocked;

  // 사진별 수정 권한 체크 (사진 업로더 또는 Owner만 수정 가능)
  // hasReport일 때는 UI에서 disabled + 툴팁으로 표시
  const canModifyPhoto = (photo: Photo): boolean => {
    if (isReadonly) return false;
    const isPhotoUploader = photo.addedById === currentUserId;
    return isPhotoUploader || isOwner;
  };

  // 사진별 검토 권한 체크 (사진 업로더 또는 Owner만 검토 가능)
  // hasReport일 때는 UI에서 disabled + 툴팁으로 표시
  const canReviewPhoto = (photo: Photo): boolean => {
    if (isReadonly) return false;
    const isPhotoUploader = photo.addedById === currentUserId;
    return isPhotoUploader || isOwner;
  };

  // 선택/검토 대상 사진 목록 (Owner면 전체, 아니면 본인 사진만)
  const selectablePhotos = isOwner
    ? inspection.photos
    : inspection.photos.filter((p) => p.addedById === currentUserId);
  const selectablePhotoIds = new Set(selectablePhotos.map((p) => p.id));
  const [activeInspectionTab, setActiveInspectionTab] = useState<string>('current');
  const { handleTabChange: handleInspectionTabChange } = useTabScrollMemory();
  const onInspectionTabChange = useCallback(
    (newTab: string) => handleInspectionTabChange(activeInspectionTab, newTab, setActiveInspectionTab),
    [activeInspectionTab, handleInspectionTabChange],
  );
  const [previousFindingDetail, setPreviousFindingDetail] = useState<PreviousFinding | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  // 점검완료 서명 모달
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // 점검완료 취소/요청 모달
  const [showCompletionActionModal, setShowCompletionActionModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // 위험영역 표시 상태 (공통, 기본값: true)
  const [showHazardOverlay, setShowHazardOverlay] = useState(true);
  // 그리기 메모 표시 상태
  const [showAnnotationOverlay, setShowAnnotationOverlay] = useState(true);
  // 강조된 발견사항 ID (새로 추가된 발견사항 선택용)
  const [highlightedFindingId, setHighlightedFindingId] = useState<string | null>(null);

  // 위험 영역 에디터 상태
  const [hazardEditorState, setHazardEditorState] = useState<{
    isOpen: boolean;
    currentIndex: number;
  }>({
    isOpen: false,
    currentIndex: 0,
  });

  // 위험 영역 편집 상태 (findingId -> 편집 상태)
  const [hazardEdits, setHazardEdits] = useState<Map<string, HazardEditState>>(new Map());

  // 위험요인 해소 모달 상태
  const [hazardResolutionState, setHazardResolutionState] = useState<{
    isOpen: boolean;
    photoId: string | null;
    preSelectedFindingId?: string;
  }>({ isOpen: false, photoId: null });

  // 발견사항 추가 시트 상태
  const [addFindingSheetState, setAddFindingSheetState] = useState<{
    isOpen: boolean;
    photoId: string | null;
    isLoading: boolean;
    mode: 'hazard' | 'best-practice';
  }>({ isOpen: false, photoId: null, isLoading: false, mode: 'hazard' });

  // 발견사항 추가 시트 핸들러
  const handleOpenAddFindingSheet = (photoId: string, mode: 'hazard' | 'best-practice' = 'hazard') => {
    setAddFindingSheetState({ isOpen: true, photoId, isLoading: false, mode });
  };

  const handleCloseAddFindingSheet = () => {
    setAddFindingSheetState({ isOpen: false, photoId: null, isLoading: false, mode: 'hazard' });
  };

  const handleAddFindingSubmit = async (data: {
    category: string;
    description: string;
  }) => {
    if (!addFindingSheetState.photoId || !onAddFinding) return;
    const photoId = addFindingSheetState.photoId;
    const isBpMode = addFindingSheetState.mode === 'best-practice';
    setAddFindingSheetState((s) => ({ ...s, isLoading: true }));
    try {
      const newFindingId = await onAddFinding(photoId, {
        category: data.category,
        description: data.description,
      });
      handleCloseAddFindingSheet();

      // 새 발견사항이 추가되면 선택 상태로 설정하고 위험영역 표시 활성화 (위험요인만)
      if (newFindingId && !isBpMode) {
        setHighlightedFindingId(newFindingId);
        setShowHazardOverlay(true);
      }
    } finally {
      setAddFindingSheetState((s) => ({ ...s, isLoading: false }));
    }
  };

  // (「이미 생성된 보고서 목록」을 여기서 조회하지 않는다 — 중복 생성 가드는 문서 유형 선택 모달이
  //  템플릿 키 축으로 직접 조회한다.)

  // 점검 담당자 편집 모달 상태
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);

  // 검토 요청 상태
  const [isRequestingReview, setIsRequestingReview] = useState(false);
  // 일괄 작업 확인 다이얼로그 상태
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    isOpen: boolean;
    actionType: BulkActionType;
    isLoading: boolean;
  }>({
    isOpen: false,
    actionType: 'reanalyze',
    isLoading: false,
  });
  // 금번 점검 사진 뷰어 (그리기 기능 포함)
  const [photoViewerState, setPhotoViewerState] = useState<{
    isOpen: boolean;
    currentIndex: number;
    photos: { id: string; url: string; caption?: string }[];
    forceReadonly?: boolean;
    overrideInspectionId?: string;
    skipAnnotation?: boolean;
  }>({
    isOpen: false,
    currentIndex: 0,
    photos: [],
  });

  // 계산된 값들
  const totalPhotos = inspection.photos.length;
  const reviewablePhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED'
  );
  const reviewedPhotos = inspection.photos.filter((p) => p.isReviewed);
  // 이전 미해결 항목 (이번 점검에서 해결된 것도 포함 - 서버에서 이미 필터링됨)
  const previousFindings = inspection.previousFindings;

  // 미입력 발견사항 수 (조치결과가 이번 점검에서 입력되지 않은 것)
  const unreviewedFindingsCount = previousFindings.filter(
    (f) => f.lastActionInspectionId !== inspection.id
  ).length;

  // 상세 상태 카운트
  const analyzingPhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'PENDING' || p.analysisStatus === 'ANALYZING'
  );
  const pendingReviewPhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED' && !p.isReviewed
  );
  // 다른 사용자가 업로드한 미검토 사진 (검토 요청 대상)
  const pendingReviewPhotosByOthers = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED' && !p.isReviewed && p.addedById && p.addedById !== currentUserId
  );

  // 점검 완료 가능 여부 확인 (공통 유틸리티 함수 사용)
  const completability = checkInspectionCompletability({
    totalPhotos: reviewablePhotos.length,
    reviewedPhotos: reviewedPhotos.length,
    unreviewedPreviousFindingsCount: unreviewedFindingsCount,
  }, { skipPhotoReviewCheck: findingSelectionEnabled });
  const canComplete = completability.canComplete;

  // 관리 권한자 또는 주 담당자 여부. ⚠️ prop `isOwner`는 이름만 남은 호환 축이다 — 값은
  // `useInspectionDetailPage`가 `isOrgOwner || (배정 현장 && inspection.manage)`로 넓힌 것이고
  // (2026-09-24 수용, company-rbac P5 라운드 ②), 「조직 Owner」로 읽으면 범위를 좁게 오독한다.
  const isInspectorOrOwner = inspectorId === currentUserId || isOwner;
  // 점검 담당자 편집 버튼 노출 여부 (관리 권한자 또는 주 담당자)
  const canChangeInspector = isInspectorOrOwner && !isReadonly;
  // 점검 담당자 편집 가능 여부: status !== COMPLETED && !hasReport
  const canEditParticipants = canChangeInspector && inspection.status !== 'COMPLETED' && !hasReport;
  // 현재 점검 참여자 목록 (서버 collaborators 직접 사용)
  const currentParticipants: InspectionCollaborator[] = useMemo(
    () => inspection.collaborators ?? [],
    [inspection.collaborators]
  );
  // 검토 요청 가능 여부 (다른 사용자가 업로드한 미검토 사진이 있을 때만)
  const canRequestReview = isInspectorOrOwner && !isReadonly && pendingReviewPhotosByOthers.length > 0;
  // 점검 삭제 권한 (주 담당자 또는 관리 권한자 — 위 isOwner 주석의 넓힌 축)
  const canDeleteInspection = inspection.inspectorId === currentUserId || isOwner;

  // 보고서 생성 권한: 주 담당자, Owner, 또는 현장 배정 담당자
  const canCreateReport = isInspectorOrOwner || isSiteAssigned;

  // 점검 삭제 가능 여부 + 불가 사유 (시안 `.btn-delete-insp`가 직접 쓴다)
  const deleteDisabled = isLoading || isLockedByLaterInspection || !canManageInspections;
  let deleteTooltip: string | undefined;
  if (!canManageInspections) deleteTooltip = '점검 관리 권한이 없습니다.';
  else if (isLockedByLaterInspection) deleteTooltip = '이후 차수의 점검이 존재하여 삭제할 수 없습니다.';

  const isCompletedStatus = inspection.status === 'COMPLETED';
  // 선택된 개선대책이 1개 이상 있는지 (피처 플래그 ON 시) — **살아 있는 것만** 센다.
  // 왜 세는 쪽이 걸러야 하는지, 파생이 둘·게이트가 둘인 것(이 값 ↔ 훅의 `hasAnyMeasures`)은
  // `lib/findingVisibility.ts` 모듈 JSDoc이 정본이다. 배선은
  // `lib/__tests__/findingVisibilityWiring.test.ts`가 지킨다.
  const hasAnySelectedMeasure = !findingSelectionEnabled
    || inspection.photos.some(p => hasSelectedMeasure(p.findings));

  // 보고서 포함 발견사항 수 (findingSelectionEnabled 시) — 분자·분모 둘 다 살아 있는 것만.
  // 안 걸면 「N/M건 포함」이 지운 발견사항까지 세어 사용자가 보는 숫자가 거짓이 된다.
  const totalFindingsCount = findingSelectionEnabled
    ? inspection.photos.reduce((sum, p) => sum + countActiveFindings(p.findings), 0)
    : 0;
  const includedFindingsCount = findingSelectionEnabled
    ? inspection.photos.reduce((sum, p) => sum + countSelectedMeasureFindings(p.findings), 0)
    : 0;

  // 보고서 생성 게이트 + 그 사유 — **식과 문구 둘 다 `lib/reportGate`가 정본**이다.
  // 종전엔 이 식과 아래 액션바의 사유 체인이 레이아웃 셋에 복제돼 있었고 **사본끼리 결과가
  // 달랐다**(이쪽은 OR 단축으로 `hasAnySelectedMeasure`를 아예 안 봤다). 갈림의 경위·정본 선택
  // 근거는 그 모듈 JSDoc에 있다. 여기서 식을 다시 쓰지 말 것 — 사유와 어긋나는 순간
  // 「막혔는데 이미 충족한 조건을 가리키는」 거짓 안내가 돌아온다.
  const reportGate = resolveReportGate({
    canCreateReport,
    canComplete,
    completabilityReasons: completability.reasons,
    findingSelectionEnabled,
    hasAnySelectedMeasure,
    completion: completion
      ? {
          allCompleted: completion.allCompleted,
          completedCount: completion.completedCount,
          totalCount: completion.totalCount,
        }
      : null,
  });
  const canGenerateReport = reportGate.canGenerate;

  const selectedCount = selectedPhotoIds.size;

  // 선택 토글 핸들러
  const handleSelectionChange = (photoId: string, checked: boolean) => {
    setSelectedPhotoIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(photoId);
      } else {
        newSet.delete(photoId);
      }
      return newSet;
    });
  };

  // 일괄 작업 확인 다이얼로그 열기
  const openBulkActionDialog = (actionType: BulkActionType) => {
    if (selectedCount > 0) {
      setBulkActionDialog({ isOpen: true, actionType, isLoading: false });
    }
  };

  // 일괄 작업 확인 다이얼로그 닫기
  const closeBulkActionDialog = () => {
    setBulkActionDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // 일괄 작업 실행
  const handleBulkActionConfirm = async () => {
    const photoIds = Array.from(selectedPhotoIds);
    setBulkActionDialog((prev) => ({ ...prev, isLoading: true }));

    try {
      switch (bulkActionDialog.actionType) {
        case 'reanalyze':
          onBulkReanalyze(photoIds);
          break;
        case 'delete':
          onSelectiveDelete(photoIds);
          break;
        case 'review':
          onSelectiveReview(photoIds);
          break;
      }
      // 작업 후 선택 해제
      setSelectedPhotoIds(new Set());
      closeBulkActionDialog();
    } catch {
      setBulkActionDialog((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // 선택 재분석 핸들러
  const handleSelectiveReanalyze = () => {
    openBulkActionDialog('reanalyze');
  };

  // 선택 검토 완료 핸들러
  const handleSelectiveReview = () => {
    openBulkActionDialog('review');
  };

  // 선택 삭제 핸들러
  const handleSelectiveDeleteClick = () => {
    openBulkActionDialog('delete');
  };

  // 점검 담당자 편집 모달 열기
  const handleOpenParticipantsModal = () => {
    setParticipantsModalOpen(true);
  };

  // 검토 요청 실행
  const handleRequestReviewClick = async () => {
    if (!onRequestReview) return;
    setIsRequestingReview(true);
    try {
      const result = await onRequestReview();
      if (result) {
        // 성공 시 알림 (toast는 상위에서 처리)
        alert(`${result.notifiedUsers.length}명에게 검토 요청을 보냈습니다.`);
      }
    } finally {
      setIsRequestingReview(false);
    }
  };

  // 전체 선택/해제 핸들러 (본인이 업로드한 사진만)
  const handleSelectAll = () => {
    // 본인이 업로드한 사진 중 선택된 개수 확인
    const mySelectedCount = Array.from(selectedPhotoIds).filter((id) => selectablePhotoIds.has(id)).length;
    if (mySelectedCount === selectablePhotos.length && selectablePhotos.length > 0) {
      // 전체 해제
      setSelectedPhotoIds(new Set());
    } else {
      // 전체 선택 (본인이 업로드한 사진만)
      setSelectedPhotoIds(new Set(selectablePhotos.map((p) => p.id)));
    }
  };

  // 본인이 업로드한 사진이 모두 선택되었는지 확인
  const mySelectedCount = Array.from(selectedPhotoIds).filter((id) => selectablePhotoIds.has(id)).length;
  const isAllSelected = mySelectedCount === selectablePhotos.length && selectablePhotos.length > 0;

  // 사진 뷰어 열기
  const openPhotoViewer = (photos: Photo[], index: number) => {
    setPhotoViewerState({
      isOpen: true,
      currentIndex: index,
      // 캡션은 **세는 자리**다 — 지운 발견사항을 세면 「3건 발견」인데 화면엔 1건만 보인다.
      photos: photos.map((p) => {
        const activeCount = countActiveFindings(p.findings);
        return {
          id: p.id,
          url: p.url,
          caption: activeCount > 0 ? `${activeCount}건 발견` : undefined,
        };
      }),
    });
  };

  // 위험 영역 변경 핸들러
  const handleHazardChange = (findingId: string, boundingBox: BoundingBox | null) => {
    setHazardEdits((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(findingId);

      const allFindings = inspection.photos.flatMap((p) =>
        p.findings.map((f) => ({
          ...f,
          aiAnalysisDetail: f.aiAnalysisDetail,
        }))
      );
      const fullFinding = allFindings.find((f) => f.id === findingId);
      const originalBox = fullFinding?.aiAnalysisDetail?.boundingBox || null;

      if (boundingBox === null) {
        newMap.set(findingId, {
          findingId,
          original: existing?.original || originalBox,
          current: null,
          isDeleted: true,
        });
      } else {
        newMap.set(findingId, {
          findingId,
          original: existing?.original || originalBox,
          current: boundingBox,
          isDeleted: false,
        });
      }

      return newMap;
    });
  };

  // 위험 영역 되돌리기 핸들러 (원본 AI 분석 결과로 복원)
  const handleHazardRestore = async (findingId: string) => {
    // 1. 로컬 편집 상태에서 원본 가져오기 (실시간 편집 중인 경우)
    const edit = hazardEdits.get(findingId);
    let originalBoundingBox = edit?.original;

    // 2. 로컬 상태에 없으면 서버의 modifications에서 원본 가져오기 (페이지 재방문 시)
    if (!originalBoundingBox) {
      const finding = inspection?.photos
        .flatMap(p => p.findings)
        .find(f => f.id === findingId);
      const boundingBoxMod = finding?.modifications?.modifiedFields?.find(f => f.field === 'boundingBox');
      if (boundingBoxMod?.originalValue) {
        originalBoundingBox = boundingBoxMod.originalValue as BoundingBox;
      }
    }

    if (!originalBoundingBox) return;

    // API로 원본 boundingBox 복원
    if (findingEditHandlers?.onUpdateFinding) {
      try {
        // 서버에 필요한 필드만 전송 (label 등 추가 속성 제외)
        const sanitizedBox = {
          x: originalBoundingBox.x,
          y: originalBoundingBox.y,
          width: originalBoundingBox.width,
          height: originalBoundingBox.height,
        };
        await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: sanitizedBox }, { isRevert: true });
      } catch (error) {
        console.error('위험 영역 되돌리기 실패:', error);
        return;
      }
    }

    // 로컬 편집 상태 초기화
    setHazardEdits((prev) => {
      const newMap = new Map(prev);
      newMap.delete(findingId);
      return newMap;
    });
  };

  // 위험 영역 변경 완료 핸들러 (드래그 종료 시 서버 저장용)
  const handleHazardChangeEnd = async (findingId: string, boundingBox: BoundingBox) => {
    if (!findingEditHandlers?.onUpdateFinding) return;
    try {
      // 서버에 필요한 필드만 전송 (label 등 추가 속성 제외)
      const sanitizedBox = {
        x: boundingBox.x,
        y: boundingBox.y,
        width: boundingBox.width,
        height: boundingBox.height,
      };
      await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: sanitizedBox });
      // 편집 상태는 유지 (되돌리기 버튼 표시용)
    } catch (error) {
      console.error('위험 영역 저장 실패:', error);
    }
  };

  // 위험 영역 삭제 저장 핸들러 (삭제 버튼 클릭 시 서버 저장용)
  const handleHazardDeleteSave = async (findingId: string) => {
    if (!findingEditHandlers?.onUpdateFinding) return;
    try {
      await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: null });
      // 편집 상태는 유지 (되돌리기 버튼 표시용)
    } catch (error) {
      console.error('위험 영역 삭제 저장 실패:', error);
    }
  };

  // PhotoViewerWithEditor용 데이터 변환
  const photosWithFindings = inspection.photos.map((photo) => ({
    photo: {
      id: photo.id,
      inspectionId: inspection.id,
      url: photo.url,
      thumbnailUrl: photo.thumbnailUrl,
      status: photo.status,
      analysisStatus: photo.analysisStatus as LibAnalysisStatus,
      isReviewed: photo.isReviewed,
      createdAt: inspection.createdAt,
    },
    findings: photo.findings.map((f) => ({
      id: f.id,
      inspectionId: inspection.id,
      photoId: photo.id,
      category: f.category,
      subCategory: f.subCategory,
      description: f.description,
      severity: f.severity as LibFindingSeverity,
      aiGenerated: f.aiGenerated,
      resolutionStatus: 'PENDING' as const,
      createdAt: inspection.createdAt,
      deletedAt: f.deletedAt,
      aiAnalysisDetail: f.aiAnalysisDetail,
    })),
  }));

  return (
    <div className="min-h-screen bg-surface">
      {/* 서브헤더 — 시안 `.insp-subheader`. 정적(비sticky)이라 스크롤하면 올라가고,
          고정되는 것은 아래의 탭 바·섹션 헤더뿐이다. */}
      <div className="mx-auto max-w-7xl px-4 pt-5 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between gap-5 pb-3.5">
          <div>
            <button
              type="button"
              onClick={onBack}
              className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-[650] tracking-[-0.005em] text-ink-500 transition-colors hover:text-brand-600"
            >
              <ChevronLeftIcon className="h-3.5 w-3.5" />
              스케줄 관리
            </button>
            <div className="flex flex-wrap items-baseline gap-3.5">
              <h1 className="text-[26px] font-extrabold leading-[1.2] tracking-[-0.025em] text-ink-900">
                {inspection.siteName} {inspection.sequenceNumber ?? 1}차 점검
              </h1>
              <span className="text-sm font-semibold tabular-nums text-ink-500">
                {formatDateToLocal(new Date(inspection.createdAt))}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {/* 현재 보고 있는 사용자 — 시안 `.view-count`(hover 시 이름 목록) */}
            {viewers.length > 0 && (
              <div className="group relative">
                <span className="inline-flex items-center gap-1.5 rounded-card-sm border border-line bg-surface-soft px-3 py-[7px] text-[13.5px] font-[650] text-ink-600">
                  <EyeIcon className="h-[15px] w-[15px] text-ink-400" />
                  <b className="font-bold tabular-nums text-ink-800">{viewers.length}</b>
                </span>
                <div className="invisible absolute right-0 top-full z-50 mt-2 min-w-max rounded-card bg-ink-800 px-3 py-2 text-sm text-white opacity-0 shadow-card transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="mb-1 text-xs text-ink-400">현재 보고 있는 사용자</div>
                  <ul className="space-y-1">
                    {viewers.map((viewer) => (
                      <li key={viewer.id}>{viewer.name}</li>
                    ))}
                  </ul>
                  <div className="absolute -top-1 right-4 h-2 w-2 rotate-45 bg-ink-800"></div>
                </div>
              </div>
            )}

            {/* 상태 pill — 시안 `.status-pill`. 진행중은 blue + 호흡하는 점,
                완료는 기존 색 대응(emerald)을 유지하며 골격만 공유한다. */}
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-bold tracking-[-0.005em] ${
                isCompletedStatus
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                  : 'border-blue-200 bg-blue-50 text-blue-600'
              }`}
            >
              <span
                className={`h-[7px] w-[7px] flex-none rounded-full ${
                  isCompletedStatus
                    ? 'bg-emerald-600'
                    : 'bg-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,.18)] motion-safe:animate-breathe'
                }`}
              />
              {isCompletedStatus ? '완료' : '진행중'}
            </span>

            {/* 점검 삭제 — 시안 `.btn-delete-insp` */}
            {canDeleteInspection && (
              deleteDisabled && deleteTooltip ? (
                <PortalTooltip content={deleteTooltip} placement="top" align="right">
                  <div>
                    <button type="button" disabled className={DELETE_INSPECTION_BUTTON_CLASS}>
                      <TrashIcon className="h-3.5 w-3.5" />
                      점검 삭제
                    </button>
                  </div>
                </PortalTooltip>
              ) : (
                <button
                  type="button"
                  onClick={onDeleteInspection}
                  disabled={deleteDisabled}
                  className={DELETE_INSPECTION_BUTTON_CLASS}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  점검 삭제
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* SCHEDULED 상태 배너 */}
      {inspection.status === 'SCHEDULED' && (
        <>
          {inspection.blockingInspectionId && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 sm:px-6 lg:px-8 py-3">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <span className="text-sm text-yellow-800">
                  이전 {inspection.blockingInspectionOrder ? `${inspection.blockingInspectionOrder}차 ` : ''}점검을 완료하거나 삭제해야 시작할 수 있습니다.
                </span>
                <Link
                  to={`/inspections/${inspection.blockingInspectionId}`}
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium flex-shrink-0 ml-4"
                >
                  이전 점검 보기 →
                </Link>
              </div>
            </div>
          )}
          <div className="bg-violet-50 border-b border-violet-200 px-4 sm:px-6 lg:px-8 py-3">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div>
                <Badge tone="violet" className="mr-3">
                  예약 점검
                </Badge>
                <span className="text-sm text-violet-700">
                  {inspection.scheduledAt ? (
                    canStartScheduledInspection(inspection.scheduledAt)
                      ? '오늘 점검을 시작할 수 있습니다.'
                      : '예약일 이후에 점검을 시작할 수 있습니다.'
                  ) : '점검 시작 준비가 되면 시작하기 버튼을 누르세요.'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* 변경 요청 버튼 (안전관리자 + Owner 배정 점검).
                    아웃라인은 tone 축이 아직 안 담는다(tone=솔리드 채움 전용) — 색 계열만 violet으로 맞춘다. */}
                {canRequestScheduleChange && (
                  <button
                    type="button"
                    onClick={() => setShowScheduleChangeModal(true)}
                    className="px-3 py-1.5 bg-white border border-violet-300 text-violet-700 text-sm rounded-control hover:bg-violet-50 font-medium transition-colors"
                  >
                    변경 요청
                  </button>
                )}
                {onStartScheduled && (() => {
                  const canStart = canStartScheduledInspection(inspection.scheduledAt) && !inspection.blockingInspectionId;
                  return canStart ? (
                    <Button tone="violet" size="sm" onClick={onStartScheduled}>
                      점검 시작
                    </Button>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
        </>
      )}

      {/* 메인 콘텐츠 */}
      {/* 상단 여백은 서브헤더가 이미 냈다(시안 `.main{padding:20px 28px 28px}`). */}
      {/* 하단 여백은 떠 있는 액션바 카드(약 66px)와 바닥 간격 16px을 합친 값 — 스크롤 중
          카드가 콘텐츠 위에 겹쳐 서므로 마지막 카드가 가리지 않게 비워 둔다. */}
      <main className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        {/* 기여자 바 */}
        {/* 담당자 행 — 시안 `.insp-owner-row` */}
        <div className="flex items-center justify-between border-b border-line-soft pb-[18px] pt-[13px]">
          <ContributorsBar
            inspectorName={inspection.inspectorName}
            contributors={inspection.contributors}
            maxDisplay={5}
          />
          <div className="flex items-center gap-1.5">
            {/* 현장 담당자 버튼 */}
            {onOpenSiteContactsModal && !isReadonly && (
              <button type="button" onClick={onOpenSiteContactsModal} className={ROLE_TAB_CLASS}>
                <UserIcon className="h-[15px] w-[15px] text-ink-400" />
                현장 담당자
              </button>
            )}
            {/* 점검 담당자 버튼 (관리 권한자 또는 주 담당자만 — e2e `inspection-contributor-edit` 2단계가 못 박는다) */}
            {canChangeInspector && (
              canEditParticipants ? (
                <button type="button" onClick={handleOpenParticipantsModal} className={ROLE_TAB_CLASS}>
                  <UsersIcon className="h-[15px] w-[15px] text-ink-400" />
                  점검 담당자
                </button>
              ) : (
                <PortalTooltip
                  content={hasReport ? '보고서가 생성되어 점검 담당자를 편집할 수 없습니다' : '점검 완료 후에는 점검 담당자를 편집할 수 없습니다'}
                  placement="top"
                >
                  <div>
                    <button type="button" disabled className={ROLE_TAB_CLASS}>
                      <UsersIcon className="h-[15px] w-[15px] text-ink-400" />
                      점검 담당자
                    </button>
                  </div>
                </PortalTooltip>
              )
            )}
          </div>
        </div>

        {/* 스케줄 변경 요청 패널 (SCHEDULED) */}
        {inspection.status === 'SCHEDULED' && scheduleChangeRequests.length > 0 && (
          <div className="mb-4">
            <ScheduleChangeRequestPanel
              inspectionId={inspection.id}
              requests={scheduleChangeRequests}
              isOwner={isOwner}
              onResponded={() => {
                refetchScheduleChangeRequests();
                queryClient.invalidateQueries({ queryKey: ['inspection', inspection.id] });
              }}
            />
          </div>
        )}

        {/* 이전 미해결 / 금번 점검 탭 (Sticky) — 시안 `.insp-tabs`.
            공용 `SectionTabBar`는 전폭 균등 분할이라 시안의 좌측 정렬 컴팩트 탭을 낼 수 없고,
            그 컴포넌트는 다른 페이지도 쓰므로 여기서만 같은 골격(tablist/밑줄/카운트)으로 그린다. */}
        {previousFindings.length > 0 && (
          <div ref={tabBarRef} className="sticky z-[25] bg-surface -mx-4 mt-[18px] px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" style={{ top: sectionStickyTop }}>
            <nav className="flex items-center gap-1 border-b border-line" role="tablist">
              {[
                { key: 'previous', label: '이전 미해결', count: unreviewedFindingsCount },
                { key: 'current', label: '금번 점검', count: totalPhotos },
              ].map((tab) => {
                const isActive = tab.key === activeInspectionTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onInspectionTabChange(tab.key)}
                    className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-[18px] py-[13px] text-[14.5px] font-[650] tracking-[-0.01em] transition-colors ${
                      isActive
                        ? 'border-brand-600 text-brand-600'
                        : 'border-transparent text-ink-500 hover:text-ink-800'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span
                        className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-full px-[7px] text-[11.5px] font-bold tabular-nums ${
                          isActive ? 'bg-brand-50 text-brand-700' : 'bg-surface-soft text-ink-500'
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* 이전 미해결 콘텐츠 */}
        <section className="mb-8" style={{ display: previousFindings.length > 0 && activeInspectionTab === 'previous' ? '' : 'none' }}>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {previousFindings.map((finding) => (
                <PreviousFindingCard
                  key={finding.id}
                  finding={finding}
                  riskConfig={inspection.effectiveRiskAssessmentConfig}
                  currentInspectionId={inspection.id}
                  currentUserId={currentUserId}
                  isOwner={isOwner}
                  inspectorId={inspectorId}
                  hasReport={hasReport || isReadonly}
                  nextInspectionHasReport={inspection.nextInspectionHasReport}
                  isScheduled={isScheduled}
                  layout="compact"
                  nextInspectionDate={inspection.nextInspectionDate}
                  inspectionCreatedAt={inspection.createdAt}
                  onPhotoClick={(url) => {
                    setPhotoViewerState({
                      isOpen: true,
                      currentIndex: 0,
                      photos: [{ id: finding.photoId || finding.id, url, caption: finding.description }],
                      forceReadonly: true,
                      overrideInspectionId: finding.sourceInspectionId,
                    });
                  }}
                  onResolutionPhotosClick={(photos, startIndex) => {
                    setPhotoViewerState({
                      isOpen: true,
                      currentIndex: startIndex,
                      photos,
                      forceReadonly: true,
                      skipAnnotation: true,
                    });
                  }}
                  onDetailClick={() => setPreviousFindingDetail(finding)}
                />
              ))}
            </div>
          </section>

        {/* 금번 점검 사진 섹션 */}
        <InspectionPhotoSection
          stickyTop={previousFindings.length > 0 ? tabBarBottom : sectionStickyTop}
          visible={previousFindings.length === 0 || activeInspectionTab === 'current'}
          inspectionId={inspection.id}
          allPhotos={inspection.photos}
          hazardPhotos={hazardPhotos}
          bestPracticePhotos={bestPracticePhotos}
          contributorCount={inspection.contributors?.length ?? 0}
          showHazardOverlay={showHazardOverlay}
          onShowHazardOverlayChange={setShowHazardOverlay}
          showAnnotationOverlay={showAnnotationOverlay}
          onShowAnnotationOverlayChange={setShowAnnotationOverlay}
          canEdit={canEdit}
          canEditFinding={canEditFinding}
          canEditHazard={canEditHazard}
          isCompletionLocked={isCompletionLocked}
          hasReport={hasReport}
          isLockedByLaterInspection={isLockedByLaterInspection}
          isLoading={isLoading}
          enableAIAnalysis={enableAIAnalysis}
          setEnableAIAnalysis={setEnableAIAnalysis}
          focusCategories={focusCategories}
          setFocusCategories={setFocusCategories}
          onAddPhoto={onAddPhoto}
          onAddBestPracticePhoto={onAddBestPracticePhoto}
          onOpenCamera={onOpenCamera}
          onPhotoReview={onPhotoReview}
          onPhotoDelete={onPhotoDelete}
          onPhotoReanalyze={onPhotoReanalyze}
          onPhotoReanalyzeWithRegion={onPhotoReanalyzeWithRegion}
          onPhotoDownload={onPhotoDownload}
          completion={completion}
          findingSelectionEnabled={findingSelectionEnabled}
          onToggleMeasureSelection={onToggleMeasureSelection}
          selectedPhotoIds={selectedPhotoIds}
          onSelectionChange={handleSelectionChange}
          canReviewPhoto={canReviewPhoto}
          canModifyPhoto={canModifyPhoto}
          findingEditHandlers={findingEditHandlers}
          highlightedFindingId={highlightedFindingId}
          onHighlightedFindingIdChange={setHighlightedFindingId}
          hazardEdits={hazardEdits}
          onHazardChange={handleHazardChange}
          onHazardChangeEnd={handleHazardChangeEnd}
          onHazardDeleteSave={handleHazardDeleteSave}
          onHazardRestore={handleHazardRestore}
          hazardResolutionEnabled={true}
          onOpenHazardResolution={(photoId, findingId) => setHazardResolutionState({ isOpen: true, photoId, preSelectedFindingId: findingId })}
          openPhotoViewer={openPhotoViewer}
          hasAddFinding={!!onAddFinding}
          onOpenAddFindingSheet={handleOpenAddFindingSheet}
          bestPracticeEnabled={true}
          companyId={companyId}
          siteId={inspection.siteId}
          onPhotoMetaUpdate={onPhotoMetaUpdate}
          activeInspectionTab={activeInspectionTab}
          currentUserId={currentUserId}
          isPrimary={isPrimary}
          isOwner={isOwner}
          riskConfig={inspection.effectiveRiskAssessmentConfig}
          riskAssessmentConfigId={inspection.riskAssessmentConfigId}
          allRiskConfigs={allRiskConfigs}
          onRiskConfigChange={handleRiskConfigChange}
          // 완료된 점검은 평가 방식을 잠근다 — 바뀌면 아직 박제되지 않은 보고서의 등급이 따라
          // 움직인다(2026-08-06 사용자 확정). 표시는 남기고 편집만 막는다(서버도 동일하게 거부).
          canEditRiskConfig={canEdit && inspection.status !== 'COMPLETED'}
        />
      </main>

      {/* 하단 액션 바 (Desktop은 sticky 사용) - 읽기전용이 아니면 항상 표시 (SCHEDULED는 점검 시작 버튼 필요) */}
      {(!isReadonly || isScheduled) && (
        <footer className="sticky bottom-4 z-30 mt-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className={ACTION_BAR_CARD_CLASS}>
              {/* 좌측 그룹(시안 `.ab-left`): 선택 액션 + 상태 (편집 가능 시에만) */}
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* 편집 버튼들 (보고서 미생성 + 점검완료 전에만 표시) */}
                  {!isEditDisabled && !isCompletionLocked && (
                    <>
                      {/* 전체 선택 — 시안 `.chk-label`. 선택 여부는 체크박스가 나르므로
                          라벨을 '전체 해제'로 뒤집지 않는다(토글 동작은 그대로). */}
                      <label
                        title={selectablePhotos.length === 0 ? (isOwner ? '사진이 없습니다' : '본인이 업로드한 사진이 없습니다') : undefined}
                        className={`inline-flex select-none items-center gap-2 text-[13.5px] font-semibold tracking-[-0.005em] text-ink-700 ${
                          isLoading || selectablePhotos.length === 0
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          disabled={isLoading || selectablePhotos.length === 0}
                          className="m-0 h-[17px] w-[17px] rounded-[5px] accent-brand-600 disabled:cursor-not-allowed"
                        />
                        전체 선택 ({selectablePhotos.length})
                      </label>

                      {/* 선택 재분석 버튼 */}
                      <button
                        type="button"
                        onClick={handleSelectiveReanalyze}
                        disabled={isLoading || isReanalyzing || selectedCount === 0}
                        className={ACTION_BAR_GHOST_CLASS}
                      >
                        {isReanalyzing ? (
                          <>
                            <HourglassIcon className="h-[13px] w-[13px] animate-spin" />
                            {reanalysisProgress
                              ? `재분석(${reanalysisProgress.current}/${reanalysisProgress.total})`
                              : '재분석 중...'}
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="h-[13px] w-[13px]" />
                            재분석({selectedCount})
                          </>
                        )}
                      </button>

                      {/* 선택 검토 완료 버튼 (findingSelectionEnabled 시 숨김) */}
                      {!findingSelectionEnabled && (
                        <button
                          type="button"
                          onClick={handleSelectiveReview}
                          disabled={isLoading || selectedCount === 0}
                          className={ACTION_BAR_GHOST_CLASS}
                        >
                          <CheckIcon className="h-[13px] w-[13px]" />
                          검토({selectedCount})
                        </button>
                      )}

                      {/* 선택 삭제 버튼 */}
                      <button
                        type="button"
                        onClick={handleSelectiveDeleteClick}
                        disabled={isLoading || selectedCount === 0}
                        className={ACTION_BAR_GHOST_CLASS}
                      >
                        <TrashIcon className="h-[13px] w-[13px]" />
                        삭제({selectedCount})
                      </button>
                    </>
                  )}

                  {/* 수정 불가 안내 */}
                  {isEditDisabled && (
                    <div className="flex items-center gap-2 rounded-card border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-700">
                      {hasReport
                        ? <span>보고서가 생성되어 수정할 수 없습니다</span>
                        : <span>이후 차수의 점검이 존재하여 수정할 수 없습니다.</span>}
                    </div>
                  )}

                  {/* 검토 진행률 표시 (findingSelectionEnabled 시 분석중만 표시).
                      한 줄 카드라 래퍼 div를 두지 않는다 — 비었을 때 빈 래퍼가 gap을 만든다. */}
                  {analyzingPhotos.length > 0 && (
                    <span className="text-sm text-brand-600">
                      <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                      분석중 {analyzingPhotos.length}건
                    </span>
                  )}
                  {!findingSelectionEnabled && pendingReviewPhotos.length > 0 && (
                    <span className="text-sm text-amber-600">
                      검토대기 {pendingReviewPhotos.length}건
                    </span>
                  )}
                  {!findingSelectionEnabled && reviewedPhotos.length > 0 && (
                    <span className="text-sm text-emerald-600">
                      검토완료 {reviewedPhotos.length}건
                    </span>
                  )}
                  {!findingSelectionEnabled && reviewedPhotos.length >= reviewablePhotos.length && reviewablePhotos.length > 0 && analyzingPhotos.length === 0 && (
                    <CheckIcon className="h-4 w-4 text-emerald-600" />
                  )}
                </div>
              )}

              {/* 우측 그룹(시안 `.ab-right`): 안내 문구 + 완료/보고서 액션.
                  좌측 그룹이 없을 때(비편집자)도 우측으로 붙도록 ml-auto. */}
              {isScheduled ? (
                <div className="ml-auto flex flex-wrap items-center gap-2.5">
                  {onStartScheduled && (() => {
                    const canStart = canStartScheduledInspection(inspection.scheduledAt) && !inspection.blockingInspectionId;
                    return canStart ? (
                      <Button tone="violet" size="lg" onClick={onStartScheduled}>
                        점검 시작
                      </Button>
                    ) : (
                      <span className="text-sm text-ink-400">
                        {inspection.blockingInspectionId ? '이전 점검을 완료하거나 삭제해야 합니다.' : '예약일 이후에 점검을 시작할 수 있습니다.'}
                      </span>
                    );
                  })()}
                </div>
              ) : (
                <div className="ml-auto flex flex-wrap items-center gap-2.5">
                  {/* 상태/안내 표시 — ⚠️ **상태를 반영한다.** 종전엔
                      「…개선대책을 선택하세요」가 선택 진행과 **무관하게 상시** 떠 있어서,
                      12/12을 다 고른 뒤에도 그 문구만 보이고 실제로 막는 것(서명 0/2)은 화면에
                      없었다(2026-10-08 dev 실측). 그래서 **막혀 있으면 막는 사유를 그대로** 보이고,
                      다 골랐으면 완료를 말한다. 사유 문구는 `lib/reportGate`가 정본이라
                      버튼 툴팁·`aria-describedby`와 **같은 문자열**이다. */}
                  {findingSelectionEnabled ? (
                    !isEditDisabled ? (
                      reportGate.disabledReason ? (
                        <span className={`${ACTION_HINT_CLASS} text-ink-500`} role="status">
                          {reportGate.disabledReason}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 ${ACTION_HINT_CLASS} text-emerald-600`} role="status">
                          <CheckIcon className="h-3.5 w-3.5" />
                          보고서를 생성할 수 있습니다
                        </span>
                      )
                    ) : null
                  ) : inspection.status === 'COMPLETED' ? (
                    <span className={`inline-flex items-center gap-1.5 ${ACTION_HINT_CLASS} text-emerald-600`}>
                      <CheckIcon className="h-3.5 w-3.5" />
                      모든 검토가 완료되었습니다
                    </span>
                  ) : null}

                  {/* 점검완료 현황 표시 (피처 플래그 ON)
                      ⚠️ 종전 게이트는 `totalCount > 1`이라 **기여자가 한 명이면 현황이 0개**였다 —
                      그때도 서명이 보고서 생성을 막으므로 사용자는 막힌 이유를 숫자로 볼 수 없었다.
                      그래서 「아직 다 안 모였으면」 인원수와 무관하게 보인다. */}
                  {completion?.completionSignatureEnabled && completion.totalCount > 0
                    && (!completion.allCompleted || completion.totalCount > 1) && (
                    <span className={`${ACTION_HINT_CLASS} ${completion.allCompleted ? 'text-emerald-600' : 'text-ink-500'}`}>
                      점검완료 {signatureProgressLabel(completion)}
                    </span>
                  )}

                  {/* 검토 요청 버튼 (미검토 사진이 있을 때만, findingSelectionEnabled 시 숨김) */}
                  {!findingSelectionEnabled && canRequestReview && onRequestReview && (
                    <button
                      type="button"
                      onClick={handleRequestReviewClick}
                      disabled={isRequestingReview || isLoading}
                      className="inline-flex h-[38px] items-center gap-1.5 rounded-control bg-amber-50 px-3.5 text-[13.5px] font-[650] tracking-[-0.005em] text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
                    >
                      {isRequestingReview ? (
                        <>
                          <HourglassIcon className="h-3.5 w-3.5 animate-spin" />
                          요청 중...
                        </>
                      ) : (
                        <>
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          검토 요청 ({pendingReviewPhotosByOthers.length}건 미검토)
                        </>
                      )}
                    </button>
                  )}

                  {/* 점검완료 서명 버튼 */}
                  {completion?.completionSignatureEnabled && completion.canSign && (
                    (() => {
                      const btn = (
                        <button
                          type="button"
                          onClick={() => setShowSignatureModal(true)}
                          disabled={completion.isSubmitting || !completion.canSign}
                          className={ACTION_BAR_COMPLETE_CLASS}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          점검완료
                        </button>
                      );
                      if (completion.signDisabledReason) {
                        return (
                          <PortalTooltip content={completion.signDisabledReason} placement="top">
                            <div>{btn}</div>
                          </PortalTooltip>
                        );
                      }
                      return btn;
                    })()
                  )}

                  {/* 점검완료 취소/요청 버튼 */}
                  {completion?.completionSignatureEnabled && (completion.canCancel || completion.canRequest) && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        if (completion.totalCount <= 1) {
                          completion.handleCancelMyCompletion();
                        } else {
                          setShowCompletionActionModal(true);
                        }
                      }}
                      disabled={completion.isSubmitting || isLockedByLaterInspection}
                    >
                      {completion.totalCount <= 1 ? '점검완료 취소' : '점검완료 취소/요청'}
                    </Button>
                  )}

                  {/* 보고서 생성 버튼 - 비활성화 시 툴팁 표시 */}
                  {(() => {
                    const disabledReason = reportGate.disabledReason;

                    const findingsLabel = findingSelectionEnabled && totalFindingsCount > 0
                      ? `${includedFindingsCount}/${totalFindingsCount}건 포함`
                      : '';
                    const completionLabel = completion?.completionSignatureEnabled && !completion.allCompleted && completion.totalCount > 1
                      ? `${signatureProgressLabel(completion)} 완료`
                      : '';
                    const reportSuffix = completionLabel || findingsLabel;
                    const reportLabel = reportSuffix ? `보고서 생성 (${reportSuffix})` : '보고서 생성';

                    const button = (
                      <button
                        type="button"
                        onClick={() => setShowReportModal(true)}
                        disabled={isLoading || !canGenerateReport}
                        // ⚠️ **사유를 DOM에 싣는다 — 툴팁만으로는 전달되지 않는다.** rc
                        // `Tooltip`(=`PortalTooltip`)은 `onMouseEnter`/터치만 쓰고 `aria`·`role`·
                        // focus 처리가 **0개**라, 사유가 **hover 전에는 DOM에 존재하지 않는다**.
                        // 2026-10-08 dev 실측에서 이 버튼의 `title`·`aria-describedby`·`aria-label`이
                        // 전부 없어 「막혔는데 사유 표시 0개」로 관측된 자리다. rc는 이 작업 범위가
                        // 아니므로 여기서 `title` + `aria-describedby`로 싣는다.
                        title={disabledReason ?? undefined}
                        aria-describedby={disabledReason ? REPORT_DISABLED_REASON_ID : undefined}
                        className={ACTION_BAR_OUTLINE_CLASS}
                      >
                        {/* 시안 `.btn-report`는 아이콘을 동반한다 — 액션바에서 라벨만 있는 버튼과
                            아이콘 버튼이 섞이면 무게가 어긋난다. */}
                        <DocumentIcon className="h-3.5 w-3.5" />
                        {reportLabel}
                      </button>
                    );

                    // 비활성화 사유가 있으면 툴팁으로 감싸기
                    if (disabledReason) {
                      return (
                        <PortalTooltip content={disabledReason} placement="top" align="right">
                          {/* disabled 버튼은 마우스 이벤트를 받지 못하므로 div로 감싸기 */}
                          <div>
                            {button}
                            {/* `aria-describedby`의 대상. 같은 사유가 위 힌트 슬롯에도 보이지만
                                그쪽은 `isEditDisabled`·플래그 게이트 뒤라 **항상 뜨지는 않는다** —
                                이 span이 사유의 유일한 상시 접근 경로다. */}
                            <span id={REPORT_DISABLED_REASON_ID} className="sr-only">
                              {disabledReason}
                            </span>
                          </div>
                        </PortalTooltip>
                      );
                    }

                    return button;
                  })()}
                </div>
              )}
            </div>
          </div>
        </footer>
      )}

      {/* 사진 뷰어 (그리기 기능 포함) */}
      <PhotoViewer
        photos={photoViewerState.photos}
        currentIndex={photoViewerState.currentIndex}
        isOpen={photoViewerState.isOpen}
        onClose={() => setPhotoViewerState((s) => ({ ...s, isOpen: false }))}
        onIndexChange={(index) =>
          setPhotoViewerState((s) => ({ ...s, currentIndex: index }))
        }
        annotation={{
          companyId: companyId || '',
          inspectionId: photoViewerState.overrideInspectionId || inspection.id,
          readonly: isReadonly || isEditDisabled || isCompletionLocked || !!photoViewerState.forceReadonly,
          initialShowAnnotation: !photoViewerState.forceReadonly,
          skipAnnotation: !!photoViewerState.skipAnnotation,
        }}
      />

      {/* 위험 영역 에디터 */}
      <PhotoViewerWithEditor
        photos={photosWithFindings}
        currentIndex={hazardEditorState.currentIndex}
        isOpen={hazardEditorState.isOpen}
        onClose={() => setHazardEditorState((s) => ({ ...s, isOpen: false }))}
        onIndexChange={(index) =>
          setHazardEditorState((s) => ({ ...s, currentIndex: index }))
        }
        onHazardChange={canEditHazard ? handleHazardChange : undefined}
        onHazardRestore={canEditHazard ? handleHazardRestore : undefined}
        hazardEdits={Array.from(hazardEdits.values())}
      />

      {/* 문서 유형 선택 모달 */}
      <ReportTypeSelectModal
        isOpen={showReportModal}
        inspectionId={inspection.id}
        companyId={companyId || ''}
        siteId={inspection.siteId}
        onClose={() => setShowReportModal(false)}
      />

      {/* 일괄 작업 확인 다이얼로그 */}
      <BulkActionConfirmDialog
        isOpen={bulkActionDialog.isOpen}
        onClose={closeBulkActionDialog}
        onConfirm={handleBulkActionConfirm}
        actionType={bulkActionDialog.actionType}
        selectedCount={selectedCount}
        isLoading={bulkActionDialog.isLoading}
      />

      {/* 점검 담당자 편집 모달 */}
      {companyId && (
        <ParticipantsEditModal
          isOpen={participantsModalOpen}
          onClose={() => setParticipantsModalOpen(false)}
          inspectionId={inspection.id}
          companyId={companyId}
          siteId={inspection.siteId}
          currentParticipants={currentParticipants}
          currentInspectorId={inspectorId ?? null}
          canEdit={canEditParticipants}
        />
      )}

      {/* 발견사항 추가 시트 */}
      {addFindingSheetState.photoId && (
        <AddFindingSheet
          isOpen={addFindingSheetState.isOpen}
          onClose={handleCloseAddFindingSheet}
          photoId={addFindingSheetState.photoId}
          onSubmit={handleAddFindingSubmit}
          isLoading={addFindingSheetState.isLoading}
          mode={addFindingSheetState.mode}
        />
      )}

      {/* 이전 미해결 상세 모달 */}
      {previousFindingDetail && (
        <PreviousFindingDetailModal
          finding={previousFindingDetail}
          onClose={() => setPreviousFindingDetail(null)}
          riskConfig={inspection.effectiveRiskAssessmentConfig}
        />
      )}

      {/* 위험요인 해소 모달 */}
      {hazardResolutionState.isOpen && hazardResolutionState.photoId && companyId && (
        <HazardResolutionModal
          isOpen={hazardResolutionState.isOpen}
          onClose={() => setHazardResolutionState({ isOpen: false, photoId: null })}
          inspectionId={inspection.id}
          companyId={companyId}
          findings={
            activeFindings(
              inspection.photos.find(p => p.id === hazardResolutionState.photoId)?.findings ?? [],
            ).map(f => ({ ...f, measures: f.aiAnalysisDetail?.recommendation?.measures }))
          }
          preSelectedFindingId={hazardResolutionState.preSelectedFindingId}
          riskConfig={inspection.effectiveRiskAssessmentConfig}
        />
      )}

      {/* 점검완료 서명 모달 */}
      {completion?.completionSignatureEnabled && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSubmit={async (signatureData) => {
            await completion.handleCompleteMyInspection({ signatureData });
            setShowSignatureModal(false);
          }}
          isSubmitting={completion.isSubmitting}
          error={completion.error}
          variant="center"
        />
      )}

      {/* 점검완료 취소/요청 모달 */}
      {/* 처리(취소·요청) 중에는 닫기 수단을 전부 막는다 — 종전엔 바깥 클릭으로 도중에 닫혔다. */}
      {showCompletionActionModal && completion?.completionSignatureEnabled && (
        <Modal
          open
          onClose={() => setShowCompletionActionModal(false)}
          title={completion.totalCount <= 1 ? '점검완료 취소' : '점검완료 취소/요청'}
          size="sm"
          overlayZClassName={MODAL_LAYER.sub}
          closeLocked={completion.isSubmitting}
        >
              <div className="space-y-3">
                {/* 완료 현황 */}
                <div className="bg-surface-soft rounded-card p-3 text-sm">
                  <p className="text-ink-600">
                    점검완료 현황: <span className="font-medium text-ink-900">{completion.completedCount}/{completion.totalCount}</span>
                  </p>
                  {completion.pendingContributors.length > 0 && (
                    <p className="mt-1 text-ink-500">
                      미완료: {completion.pendingContributors.map(c => c.userName).join(', ')}
                    </p>
                  )}
                </div>

                {/* 점검완료 취소 버튼 */}
                {completion.canCancel && (
                  <button
                    type="button"
                    disabled={completion.isSubmitting}
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-card transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    내 점검완료 취소
                  </button>
                )}

                {/* 점검완료 요청 버튼 */}
                {completion.canRequest && (
                  <button
                    type="button"
                    disabled={completion.isSubmitting}
                    onClick={async () => {
                      try {
                        const result = await completion.handleRequestCompletion();
                        if (result) {
                          const count = result.requestedCount ?? result.notifiedUsers?.length ?? 0;
                          showToast(`${count}명에게 점검완료 요청을 보냈습니다.`, 'success');
                        }
                        setShowCompletionActionModal(false);
                      } catch {
                        // error is set in the hook
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-card transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    미완료 참여자에게 요청
                  </button>
                )}

                {completion.error && (
                  <p className="text-sm text-red-600">{completion.error}</p>
                )}
              </div>
        </Modal>
      )}
      {/* 스케줄 변경 요청 모달 */}
      {canRequestScheduleChange && (
        <ScheduleChangeRequestModal
          isOpen={showScheduleChangeModal}
          onClose={() => setShowScheduleChangeModal(false)}
          inspectionId={inspection.id}
          currentScheduledAt={inspection.scheduledAt}
          onSuccess={() => {
            refetchScheduleChangeRequests();
          }}
        />
      )}

      {/* 점검완료 취소 확인 모달 */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={async () => {
          try {
            await completion?.handleCancelMyCompletion();
            setShowCancelConfirm(false);
            setShowCompletionActionModal(false);
          } catch {
            // error is set in the hook
          }
        }}
        title="점검완료 취소"
        message="점검완료를 취소하시겠습니까?"
        confirmText="취소하기"
        cancelText="닫기"
        confirmStyle="danger"
        isLoading={completion?.isSubmitting}
      />

    </div>
  );
}

export default InspectionDetailDesktop;
